import { forEach, isNil, trim } from "lodash";
import { useGrowl } from "PFApp/use_growl";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useSkillsFramework } from "PFCore/hooks/queries/admin/skills_frameworks/use_skills_framework";
import { useSkillsFrameworkCreate } from "PFCore/hooks/queries/admin/skills_frameworks/use_skills_framework_create";
import { useSkillsFrameworkUpdate } from "PFCore/hooks/queries/admin/skills_frameworks/use_skills_framework_update";
import { useCustomValuesInvalidate } from "PFCore/hooks/queries/custom_fields/admin";
import { Id } from "PFTypes/id";
import { SkillsFrameworkCreateUpdateData } from "PFTypes/skills_framework";
import { useCallback } from "react";
import { UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import { SkillsFrameworkData } from "../skills_frameworks.types";

const parseFormValuesToMutationPayload = (
  formValues: SkillsFrameworkData
): SkillsFrameworkCreateUpdateData => {
  const parsedCustomFields: SkillsFrameworkCreateUpdateData["customFields"] = [];
  const notEmptyCustomFields = formValues.customFields.filter(({ customTypeId }) => customTypeId !== null);

  notEmptyCustomFields.forEach(({ customTypeId, values }) => {
    const atomicCustomFieldsWithValues = values.map(({ id }) => ({
      customTypeId: customTypeId as Id,
      customValueId: id
    }));
    parsedCustomFields.push(...atomicCustomFieldsWithValues);
  });

  const allSkills = [...formValues.skills.essential, ...formValues.skills.supporting];
  const parsedSkills = allSkills.map(({ skill, importance, experience }) => ({
    skillId: skill.id,
    importance,
    experience
  }));

  return {
    name: trim(formValues.name),
    description: trim(formValues.description),
    customFields: parsedCustomFields,
    skills: parsedSkills,
    rank: formValues.rank
  };
};

export const useSkillsFrameworksEditPage = () => {
  const history = useHistory();
  const { t } = useTranslation("skillsFrameworks");
  const growl = useGrowl();
  const { id } = useParams<{ id: string }>();
  const {
    data: skillsFramework,
    error: fetchError,
    isFetching
  } = useSkillsFramework({ id, options: { enabled: !isNil(id) } });
  const { mutate: createSkillsFramework, isLoading: isCreating } = useSkillsFrameworkCreate();
  const { mutate: updateSkillsFramework, isLoading: isUpdating } = useSkillsFrameworkUpdate();
  const { getFormattedErrors } = useResponseErrors({
    translationNs: "skillsFrameworks",
    translationOptions: { keyPrefix: "edit.apiErrors" }
  });
  const { invalidate: invalidateCustomValues } = useCustomValuesInvalidate();

  const showErrors = useCallback(
    (errors: Record<string, string>, setError: UseFormSetError<SkillsFrameworkData>) => {
      forEach(errors, (message, fieldName) => {
        if (fieldName === "name" || fieldName === "description") {
          setError(fieldName, { type: "custom", message });
        } else {
          growl({
            message,
            kind: "error"
          });
        }
      });
    },
    [growl]
  );

  const handleSkillsFrameworkCreate = useCallback(
    (formValues: SkillsFrameworkData, setError: UseFormSetError<SkillsFrameworkData>) => {
      const payload = parseFormValuesToMutationPayload(formValues);
      createSkillsFramework(payload, {
        onSuccess: () => {
          history.push(`/admin/skills_frameworks/${payload.rank}`);
          growl({
            message: t("edit.skillFrameworkCreated", { name: payload.name })
          });
          invalidateCustomValues();
        },
        onError: ({ response }) => {
          const errors = getFormattedErrors(response.data);
          showErrors(errors, setError);
        }
      });
    },
    [createSkillsFramework, history, growl, t, invalidateCustomValues, getFormattedErrors, showErrors]
  );

  const handleSkillsFrameworkUpdate = useCallback(
    (formValues: SkillsFrameworkData, setError: UseFormSetError<SkillsFrameworkData>) => {
      const payload = parseFormValuesToMutationPayload(formValues);

      updateSkillsFramework(
        { id, data: payload },
        {
          onSuccess: () => {
            history.push(`/admin/skills_frameworks/${payload.rank}`);
            growl({
              message: t("edit.skillFrameworkEdited", { name: payload.name })
            });
            invalidateCustomValues();
          },
          onError: ({ response }) => {
            const errors = getFormattedErrors(response.data);
            showErrors(errors, setError);
          }
        }
      );
    },
    [updateSkillsFramework, id, history, growl, t, invalidateCustomValues, getFormattedErrors, showErrors]
  );

  return {
    skillsFramework: skillsFramework ?? null,
    isFetching,
    isCreating,
    isUpdating,
    fetchError,
    handleSkillsFrameworkCreate,
    handleSkillsFrameworkUpdate
  };
};
